import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { MerchantAppReturn } from "@redotech/redo-model/return";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import MailIcon from "@redotech/redo-web/icon-old/mail.svg";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { getDateString } from "@redotech/util/date-format";
import { formatSentenceCase } from "@redotech/util/string";
import classNames from "classnames";
import { memo, useState } from "react";
import { formatShippingAddressString } from "../../order/format-shipping-address";
import { ResendModal } from "../../return/return-modals/resend-modal";
import { ResetModal } from "../../return/return-modals/reset-modal";
import { FieldLabel } from "./field-label";
import * as styles from "./shared-card-components.module.css";

export interface ReturnCardProps {
  return: MerchantAppReturn;
}

export const ReturnCard = memo(function ReturnCard({
  return: returnData,
}: ReturnCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const team = useRequiredContext(TeamContext);

  const handleToggle = useHandler(() => {
    setIsExpanded((prev) => !prev);
  });

  const handleResetClose = useHandler(() => {
    setResetModalOpen(false);
  });

  const returnProvision = returnData.provision;
  const returnDate = new Date(returnData.createdAt);
  const totalRefund = returnData.products.reduce(
    (sum, product) => sum + Number(product.price) * product.quantity,
    0,
  );
  const externalUrl = `https://${team.storeUrl}/admin/apps/redo-1/returns/${returnData._id}`;
  const canReset = ![ReturnStatus.COMPLETE, ReturnStatus.DELETED].includes(
    returnData.status as ReturnStatus,
  );

  return (
    <Flex
      bgColor="secondary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="md"
      radius="md"
    >
      <Flex align="center" gap="xs" justify="space-between">
        <Flex align="center" gap="xxs" justify="center">
          <CopyableText fontSize="xs" fontWeight="medium">
            {`Return ${returnProvision}`}
          </CopyableText>
          {externalUrl && (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={LinkExternalIcon}
              onClick={() => {
                window.open(externalUrl, "_blank");
              }}
              size="xs"
            />
          )}
        </Flex>

        <span
          className={classNames(
            styles.chevron,
            isExpanded && styles.chevronRotated,
          )}
          onClick={handleToggle}
        >
          <Icon arbiterIconSvg={ChevronDown} color="ghost" size="extra-small" />
        </span>
      </Flex>

      <ReturnBadges return={returnData} />

      <Flex dir="column" gap="md">
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Created on</FieldLabel>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {getDateString(returnDate)}
          </Text>
        </Flex>

        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Total refund</FieldLabel>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {getCurrencyFormatter("USD").format(totalRefund)}
          </Text>
        </Flex>

        {returnData.orders.length > 0 && (
          <Flex align="center" gap="md" justify="space-between">
            <FieldLabel>Original order</FieldLabel>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {returnData.orders[0]?.name}
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex dir="column" gap="xs">
        <Tooltip
          title={
            canReset
              ? "Resend the return label to the customer"
              : "Cannot resend label for completed or deleted returns"
          }
        >
          <RedoButton
            hierarchy="secondary"
            IconLeading={MailIcon}
            onClick={() => setResendModalOpen(true)}
            size="xs"
            text="Resend label"
          />
        </Tooltip>
        {canReset && (
          <Tooltip title="Resetting the return will allow the customer to start the return process over. This should only be used if there was an issue with the original return, such as a lost label.">
            <RedoButton
              hierarchy="secondary"
              IconLeading={RefreshIcon}
              onClick={() => setResetModalOpen(true)}
              size="xs"
              text="Reset return"
            />
          </Tooltip>
        )}
      </Flex>

      {isExpanded && (
        <Flex dir="column" gap="md">
          {returnData.shipping_address && (
            <ReturnShippingAddress return={returnData} />
          )}

          <FieldLabel>Item(s)</FieldLabel>
          {returnData.products.map((product) => (
            <ReturnProductItem key={product._id} product={product} />
          ))}
        </Flex>
      )}

      <ResendModal
        isOpen={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        return_={returnData}
      />
      {resetModalOpen && (
        <ResetModal
          onClose={handleResetClose}
          onSubmitFinished={handleResetClose}
          open={resetModalOpen}
          resource={{
            returnId: returnData._id,
            returnType: returnData.type as any,
          }}
        />
      )}
    </Flex>
  );
});

const getReturnStatusBadgeColor = (status: string): RedoBadgeColor => {
  const lowerStatus = status.toLowerCase();

  if (lowerStatus === "complete" || lowerStatus === "delivered") {
    return "success";
  }
  if (lowerStatus === "in_transit") {
    return "gray";
  }
  if (
    lowerStatus === "flagged" ||
    lowerStatus === "rejected" ||
    lowerStatus === "cancelled"
  ) {
    return "error";
  }
  if (lowerStatus === "needs_review") {
    return "warning";
  }
  return "blueLight";
};

const getReturnTypeBadgeColor = (type: string): RedoBadgeColor => {
  const lowerType = type.toLowerCase();
  if (lowerType === "claim") {
    return "warning";
  }
  return "gray";
};

const ReturnBadges = memo(function ReturnBadges({
  return: returnData,
}: {
  return: MerchantAppReturn;
}) {
  return (
    <Flex gap="xs" wrap="wrap">
      {returnData.type && (
        <RedoBadge
          color={getReturnTypeBadgeColor(returnData.type)}
          size="sm"
          text={formatSentenceCase(returnData.type)}
        />
      )}
      {returnData.status && (
        <RedoBadge
          color={getReturnStatusBadgeColor(returnData.status)}
          size="sm"
          text={formatSentenceCase(returnData.status)}
        />
      )}
    </Flex>
  );
});

const ReturnShippingAddress = memo(function ReturnShippingAddress({
  return: returnData,
}: {
  return: MerchantAppReturn;
}) {
  const shippingAddress = formatShippingAddressString(
    returnData.shipping_address
      ? { ...returnData.shipping_address, name: null }
      : null,
  );

  if (!shippingAddress) {
    return null;
  }

  return (
    <Flex align="flex-start" gap="md" justify="space-between">
      <FieldLabel>Shipping address</FieldLabel>
      <Flex className={styles.shippingAddress}>
        <CopyableText fontSize="xs" textColor="primary">
          {shippingAddress}
        </CopyableText>
      </Flex>
    </Flex>
  );
});

const ReturnProductItem = memo(function ReturnProductItem({
  product,
}: {
  product: MerchantAppReturn["products"][number];
}) {
  const hasImage = product.images && product.images.length > 0;

  return (
    <Flex align="flex-start" gap="sm">
      <Flex
        align="center"
        bgColor={hasImage ? undefined : "tertiary"}
        borderColor="secondary"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.imageContainer}
        justify="center"
        radius="sm"
        shrink="0"
      >
        {hasImage ? (
          <img
            alt={product.product_title}
            className={styles.image}
            src={product.images[0] ?? ""}
          />
        ) : (
          <Icon arbiterIconSvg={ImageIcon} color="ghost" size="compact" />
        )}
      </Flex>

      <Flex dir="column" gap="none" grow="1">
        <Text fontSize="xs" fontWeight="regular" textColor="primary">
          {getCurrencyFormatter("USD").format(Number(product.price))}
        </Text>
        <Text fontSize="xs" fontWeight="regular" textColor="primary">
          {product.product_title}
        </Text>
        {product.variant_title && (
          <Text fontSize="xs" textColor="tertiary">
            {product.variant_title}
          </Text>
        )}
        <Text fontSize="xs" textColor="tertiary">
          Qty: {product.quantity}
        </Text>
        {product.reason && (
          <RedoBadge
            color="blue"
            size="xs"
            text={formatSentenceCase(product.reason)}
          />
        )}
        {product.strategy && (
          <RedoBadge
            color="warning"
            size="xs"
            text={formatSentenceCase(product.strategy)}
          />
        )}
      </Flex>
    </Flex>
  );
});
